import React, { useState } from 'react';
import {
  Chip,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  CircularProgress,
  Box
} from '@mui/material';
import {
  Edit as DraftIcon,
  CheckCircle as ActiveIcon,
  Cancel as TerminatedIcon,
  ExpandMore as DropdownIcon
} from '@mui/icons-material';

interface StatusDropdownProps {
  currentStatus: 'draft' | 'active' | 'terminated';
  leaseId: string;
  onStatusChange: (leaseId: string, newStatus: 'draft' | 'active' | 'terminated') => Promise<void>;
  disabled?: boolean;
}

const StatusDropdown: React.FC<StatusDropdownProps> = ({
  currentStatus,
  leaseId,
  onStatusChange,
  disabled = false
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [isUpdating, setIsUpdating] = useState(false);
  const open = Boolean(anchorEl);

  const statusConfig = {
    draft: {
      label: 'Draft',
      color: 'warning' as const,
      icon: <DraftIcon sx={{ fontSize: 16 }} />
    },
    active: {
      label: 'Active',
      color: 'success' as const,
      icon: <ActiveIcon sx={{ fontSize: 16 }} />
    },
    terminated: {
      label: 'Terminated',
      color: 'error' as const,
      icon: <TerminatedIcon sx={{ fontSize: 16 }} />
    }
  };

  const availableStatuses = ['draft', 'active', 'terminated'] as const;

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    if (!disabled && !isUpdating) {
      setAnchorEl(event.currentTarget);
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleStatusSelect = async (newStatus: 'draft' | 'active' | 'terminated') => {
    if (newStatus === currentStatus) {
      handleClose();
      return;
    }

    setIsUpdating(true);
    try {
      await onStatusChange(leaseId, newStatus);
      handleClose();
    } catch (error) {
      console.error('Failed to update status:', error);
    } finally {
      setIsUpdating(false);
    }
  };

  const currentConfig = statusConfig[currentStatus];

  return (
    <>
      <Chip
        label={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {isUpdating ? (
              <CircularProgress size={14} color="inherit" />
            ) : (
              currentConfig.icon
            )}
            {currentConfig.label}
            {!disabled && !isUpdating && (
              <DropdownIcon sx={{ fontSize: 14, ml: 0.5 }} />
            )}
          </Box>
        }
        color={currentConfig.color}
        size="small"
        onClick={handleClick}
        sx={{
          cursor: disabled || isUpdating ? 'default' : 'pointer',
          opacity: disabled ? 0.6 : 1,
          '&:hover': {
            backgroundColor: !disabled && !isUpdating ? 'action.hover' : 'inherit'
          },
          textTransform: 'capitalize',
          fontWeight: 500
        }}
        disabled={disabled || isUpdating}
      />

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        PaperProps={{
          sx: {
            mt: 1,
            minWidth: 140,
            '& .MuiMenuItem-root': {
              py: 1,
              px: 2
            }
          }
        }}
        transformOrigin={{ horizontal: 'center', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'center', vertical: 'bottom' }}
      >
        {availableStatuses.map((status) => {
          const config = statusConfig[status];
          const isCurrentStatus = status === currentStatus;
          
          return (
            <MenuItem
              key={status}
              onClick={() => handleStatusSelect(status)}
              selected={isCurrentStatus}
              sx={{
                opacity: isCurrentStatus ? 0.7 : 1,
                fontWeight: isCurrentStatus ? 600 : 400
              }}
            >
              <ListItemIcon sx={{ minWidth: 32 }}>
                {config.icon}
              </ListItemIcon>
              <ListItemText
                primary={config.label}
                sx={{
                  '& .MuiListItemText-primary': {
                    fontSize: '0.875rem',
                    fontWeight: 'inherit'
                  }
                }}
              />
            </MenuItem>
          );
        })}
      </Menu>
    </>
  );
};

export default StatusDropdown; 